import { onboardingUrl } from "@medbillai/utils/urls";

import type { User } from "../../../lib/auth/authState";

export type OnboardingGateResult = "none" | "resume";

/**
 * Acts on the latest auth snapshot, rehydrated or fresh, trusted as-is (the
 * single-session assumption): a stale fire costs one redirect that the
 * network-only resume read immediately corrects, and `undefined` fields —
 * possible only before a brand-new login's first profile — fail open.
 *
 * Terms are read directly, not inferred from identity presence: `createPerson`
 * has callers outside onboarding. No feature flags — a LaunchDarkly variation
 * can re-roll between reads.
 */
export function onboardingGate({
  user,
  pathname,
}: {
  user: User | null | undefined;
  pathname: string;
}): OnboardingGateResult {
  // The whole subtree is exempt — a user already deeper in the flow must not
  // be bounced sideways — but only slash-delimited: siblings like
  // /onboarding-help stay gated.
  const onboardingPath = onboardingUrl();
  if (
    pathname === onboardingPath ||
    pathname.startsWith(`${onboardingPath}/`)
  ) {
    return "none";
  }
  // An admin impersonating a partner account must not enter the target's
  // onboarding.
  if (user?.isShadow) {
    return "none";
  }
  if (!user?.partner) {
    return "none";
  }
  // Strictly `null`: `undefined` means unread, not unaccepted.
  return user.acceptedTermsAt === null ? "resume" : "none";
}
